import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import NavTabs from '../../components/NavTabs';
import { useAuth } from '../../context/AuthContext';
import { hasLocalKey } from '../../crypto/keystore';
import { generateAndStoreKeypair, unlockWithRecovery, rewrapAfterRecovery } from '../../crypto/keygen';
import { registerPublicKey, getMyKeyStatus } from '../../api';

// ── UI states ─────────────────────────────────────────────────────────────────
const STATE = {
  LOADING:       'loading',
  NOT_ELIGIBLE:  'not_eligible',   // not a verified org
  SETUP_NEEDED:  'setup_needed',   // no key anywhere
  RECOVERY:      'recovery',       // server has key but no local copy
  READY:         'ready',          // all set
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <svg className="animate-spin w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
    </div>
  );
}

// ── Setup flow ────────────────────────────────────────────────────────────────

const SETUP_STEP = { FORM: 'form', GENERATING: 'generating', PHRASE: 'phrase', DONE: 'done' };

function SetupFlow({ userId, onComplete }) {
  const [step,        setStep]        = useState(SETUP_STEP.FORM);
  const [password,    setPassword]    = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [phrase,      setPhrase]      = useState([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error,       setError]       = useState('');
  const [copied,      setCopied]      = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8)          return setError('Messaging password must be at least 8 characters.');
    if (password !== confirm)          return setError('Passwords do not match.');

    setStep(SETUP_STEP.GENERATING);
    try {
      const { publicJwk, recoveryPhrase } = await generateAndStoreKeypair(userId, password);
      await registerPublicKey(publicJwk);
      setPhrase(recoveryPhrase);
      setStep(SETUP_STEP.PHRASE);
    } catch (err) {
      setError('Key generation failed. Please try again.');
      setStep(SETUP_STEP.FORM);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(phrase.join(' ')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (step === SETUP_STEP.GENERATING) {
    return (
      <div className="text-center py-16">
        <Spinner />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Generating your encryption keys…</p>
      </div>
    );
  }

  if (step === SETUP_STEP.PHRASE) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-6 mb-6">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">Save your recovery phrase</p>
          <p className="text-sm text-amber-700 dark:text-amber-400 mb-4">
            This 12-word phrase is the only way to restore your inbox if you clear your browser data or switch devices.
            Write it down and store it securely. <strong>It will not be shown again.</strong>
          </p>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {phrase.map((word, i) => (
              <div key={i} className="bg-white dark:bg-gray-700 border border-amber-200 dark:border-amber-600 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 dark:text-gray-100">
                <span className="text-amber-500 text-xs mr-1">{i + 1}.</span>{word}
              </div>
            ))}
          </div>
          <button
            onClick={handleCopy}
            className="text-xs text-amber-700 dark:text-amber-400 underline hover:text-amber-900"
          >
            {copied ? 'Copied!' : 'Copy all words'}
          </button>
        </div>

        <label className="flex items-start gap-3 cursor-pointer mb-6">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 w-4 h-4 text-blue-600"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            I have saved my recovery phrase in a secure location. I understand it will not be shown again.
          </span>
        </label>

        <button
          onClick={onComplete}
          disabled={!acknowledged}
          className="w-full py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-700 text-white"
        >
          Complete setup
        </button>
      </div>
    );
  }

  // SETUP_STEP.FORM
  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Create a messaging password</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          This password protects your private encryption key stored on this device. It can be the same as your login password.
          If you ever change it, use your recovery phrase to restore access.
        </p>

        <form onSubmit={handleGenerate} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Messaging password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Confirm password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat password"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <button
            type="submit"
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
          >
            Generate my encryption keys
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Recovery flow ─────────────────────────────────────────────────────────────

function RecoveryFlow({ userId, onComplete, onRegenerate }) {
  const [phrase,      setPhrase]      = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');

  const handleRestore = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8)       return setError('New password must be at least 8 characters.');
    if (newPassword !== confirm)       return setError('Passwords do not match.');
    if (!phrase.trim())                return setError('Please enter your recovery phrase.');

    setLoading(true);
    try {
      const privateKey = await unlockWithRecovery(userId, phrase.trim());
      if (!privateKey) {
        setError('Recovery phrase is incorrect. Please check each word and try again.');
        setLoading(false);
        return;
      }
      await rewrapAfterRecovery(userId, privateKey, newPassword, phrase.trim());
      onComplete();
    } catch {
      setError('Restore failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-5 mb-5">
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">Inbox not found on this device</p>
        <p className="text-sm text-amber-700 dark:text-amber-400">
          Your encryption keys are not stored on this browser. Enter your 12-word recovery phrase to restore access,
          or generate new keys (your message history will be unreadable with new keys).
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">Restore with recovery phrase</h2>
        <form onSubmit={handleRestore} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Recovery phrase (12 words, space-separated)</label>
            <textarea
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              rows={3}
              placeholder="word1 word2 word3 …"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm font-mono bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Set new messaging password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Confirm new password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat password"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
          >
            {loading ? 'Restoring…' : 'Restore inbox'}
          </button>
        </form>
      </div>

      <button
        onClick={onRegenerate}
        className="w-full py-2.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg text-sm transition-colors"
      >
        Generate new keys instead (message history will be lost)
      </button>
    </div>
  );
}

// ── Ready state (Phase 1 placeholder) ─────────────────────────────────────────

function InboxReady() {
  return (
    <div className="max-w-lg mx-auto text-center py-16">
      <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-5">
        <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Secure inbox ready</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
        Your encryption keys are set up. Secure messaging between verified agencies is coming in the next update.
      </p>
    </div>
  );
}

// ── Not eligible state ────────────────────────────────────────────────────────

function NotEligible() {
  return (
    <div className="max-w-lg mx-auto text-center py-16">
      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-5">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">Secure inbox is for verified agencies</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
        This feature is available to org accounts that have been verified with a linked agency.
        Contact us at{' '}
        <a href="mailto:watreatmentfinder@gmail.com" className="text-blue-600 hover:underline">
          watreatmentfinder@gmail.com
        </a>{' '}
        if you believe your account should have access.
      </p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SecureInbox() {
  const { user, isOrgAccount } = useAuth();
  const [uiState, setUiState]  = useState(STATE.LOADING);

  const isVerifiedOrg = isOrgAccount && !!user?.facility_id;

  const checkState = useCallback(async () => {
    if (!isVerifiedOrg) {
      setUiState(STATE.NOT_ELIGIBLE);
      return;
    }

    try {
      const [localKey, serverStatus] = await Promise.all([
        hasLocalKey(user.id),
        getMyKeyStatus(),
      ]);

      if (localKey && serverStatus.has_key) {
        setUiState(STATE.READY);
      } else if (!localKey && serverStatus.has_key) {
        setUiState(STATE.RECOVERY);
      } else {
        setUiState(STATE.SETUP_NEEDED);
      }
    } catch {
      setUiState(STATE.SETUP_NEEDED);
    }
  }, [isVerifiedOrg, user?.id]);

  useEffect(() => { checkState(); }, [checkState]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>Secure Inbox | WA BHA Treatment Finder</title>
      </Helmet>
      <Header right={null} />
      <NavTabs />

      <div className="max-w-4xl mx-auto px-4 pt-8 pb-16">
        {/* Page header */}
        {uiState !== STATE.NOT_ELIGIBLE && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Secure Inbox</h1>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              End-to-end encrypted messaging between verified agencies. Messages are encrypted in your browser — the server never sees the content.
            </p>
          </div>
        )}

        {/* State-based content */}
        {uiState === STATE.LOADING       && <Spinner />}
        {uiState === STATE.NOT_ELIGIBLE  && <NotEligible />}
        {uiState === STATE.SETUP_NEEDED  && (
          <SetupFlow
            userId={user.id}
            onComplete={() => setUiState(STATE.READY)}
          />
        )}
        {uiState === STATE.RECOVERY && (
          <RecoveryFlow
            userId={user.id}
            onComplete={() => setUiState(STATE.READY)}
            onRegenerate={() => setUiState(STATE.SETUP_NEEDED)}
          />
        )}
        {uiState === STATE.READY && <InboxReady />}
      </div>
    </div>
  );
}
